// libraries.
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// selectors.
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import {
  getSelectedCoordinates,
  getSelectedNid,
  getSelectedBusinessLine,
} from '../store/slices/georeferencing-slice';

// hooks.
import { useGetLeadDetailInfoQuery } from '@domains/lead-detail';
import { useSaveHermesMutation } from '@domains/hermes/services/hermes.endpoints';

// utils.
import { processWebSocketMessage } from '../utils';

// types.
import { UseGeoreferencingWebSocketProps } from '../interfaces/georeferencing.types';

const selectWebSocketAction = (country?: string) => {
  switch (country) {
    case 'CO':
      return 'put_lead_co';
    case 'MX':
      return 'put_lead_mx';
    default:
      return 'put_lead_co';
  }
};

const handleError = (errorMsg: string, onError?: (error: string) => void) => {
  console.error(errorMsg);
  toast.error(errorMsg);
  if (onError) {
    onError(errorMsg);
  }
};

const buildPayload = (
  country: string | undefined,
  email: string,
  coordinates: google.maps.LatLngLiteral,
  dealId: number
) => {
  const basePayload = {
    deal_id: dealId,
    hubspot_ignore: ['dealname'],
  };

  return country === 'MX'
    ? {
        ...basePayload,
        country: 'MX',
        agent: email,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      }
    : {
        ...basePayload,
        country: 'CO',
        agente: email,
        latitud: coordinates.lat,
        longitud: coordinates.lng,
      };
};

const handleGeoreferencingSuccess = (
  setIsLoading: (loading: boolean) => void,
  refetchLeadDetail: () => void,
  onSuccess?: () => void
) => {
  setIsLoading(false);
  toast.dismiss('loadingGeoreferencing');
  toast.success('Coordenadas actualizadas', {
    toastId: 'successGeoreferencing',
  });
  refetchLeadDetail();
  if (onSuccess) {
    onSuccess();
  }
};

const handleGeoreferencingError = (
  setIsLoading: (loading: boolean) => void,
  errorMessage: string,
  onError?: (error: string) => void
) => {
  setIsLoading(false);
  toast.dismiss('loadingGeoreferencing');
  toast.error('Error al actualizar coordenadas', {
    toastId: 'errorGeoreferencing',
  });
  if (onError) {
    onError(errorMessage);
  }
};

export const useGeoreferencingWebSocket = ({
  onSuccess,
  onError,
}: UseGeoreferencingWebSocketProps) => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry);
  const email = useSelector(getEmail);
  const coordinates = useSelector(getSelectedCoordinates);
  const nid = useSelector(getSelectedNid);
  const selectedBusinessLine = useSelector(getSelectedBusinessLine);

  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const websocketResponsesRef = useRef<any[]>([]);

  const { data: leadDetail, refetch: refetchLeadDetail } =
    useGetLeadDetailInfoQuery({ country, nid: nid || '' }, { skip: !nid });

  const [saveHermes] = useSaveHermesMutation();

  // Conectar WebSocket
  useEffect(() => {
    const websocket = new WebSocket(
      process.env.ENV_WEBSOCKET_BACKBONE as string
    );
    setWs(websocket);

    return () => {
      websocket.close();
    };
  }, []);

  // Configurar eventos del WebSocket
  useEffect(() => {
    if (!ws) return;

    ws.onopen = () => {
      console.log('🔌 WebSocket conectado (Georeferencing)');
    };

    ws.onmessage = async event => {
      try {
        const parsedData = JSON.parse(event.data);

        if (!parsedData?.body) {
          console.log('⚠️ No hay "body" en parsedData');
          return;
        }

        const parsedBody = JSON.parse(parsedData.body);

        await processWebSocketMessage({
          parsedBody,
          selectedBusinessLine,
          loadingHandlers: {
            setIsLoading,
            refetchLeadDetail,
            onSuccess,
            onError,
          },
          callbacks: {
            handleGeoreferencingSuccess,
            handleGeoreferencingError,
          },
          leadData: {
            leadDetail,
            coordinates,
            email,
            country,
          },
          saveHermes,
        });

        websocketResponsesRef.current.push(parsedBody);
      } catch (error: any) {
        console.error('Error parseando respuesta del WebSocket:', error);
        handleGeoreferencingError(
          setIsLoading,
          'Error al procesar la respuesta del servidor',
          onError
        );
      }
    };

    ws.onerror = error => {
      console.error('WebSocket error:', error);
      handleGeoreferencingError(
        setIsLoading,
        'Error de conexión con el servidor',
        onError
      );
    };

    ws.onclose = () => {
      console.log('WebSocket desconectado, reintentando...');
      setWs(new WebSocket(process.env.ENV_WEBSOCKET_BACKBONE as string));
      setIsLoading(false);
    };
  }, [
    ws,
    dispatch,
    onSuccess,
    onError,
    refetchLeadDetail,
    selectedBusinessLine,
    saveHermes,
    leadDetail,
    coordinates,
    email,
    country,
  ]);

  const sendGeoreferencing = () => {
    // Validaciones con early returns (TypeScript entiende el narrowing)
    if (!ws) {
      handleError('WebSocket no está conectado', onError);
      return;
    }

    if (!leadDetail) {
      handleError('No se ha obtenido la información del lead', onError);
      return;
    }

    if (!coordinates) {
      handleError('No hay coordenadas seleccionadas', onError);
      return;
    }

    if (!email) {
      handleError('No se ha obtenido el email del agente', onError);
      return;
    }

    // Después de las validaciones, TypeScript sabe que todos los valores existen
    const payload = buildPayload(
      country,
      email,
      coordinates,
      Number(leadDetail.id)
    );
    const messageBody = JSON.stringify({
      action: selectWebSocketAction(country),
      Authorization: 'default',
      body: JSON.stringify(payload),
    });

    setIsLoading(true);
    toast.info('Actualizando información', {
      toastId: 'loadingGeoreferencing',
    });

    ws.send(messageBody);
    console.log('✉️ Mensaje enviado al WebSocket, esperando respuesta...');
  };

  return {
    sendGeoreferencing,
    isLoading,
    canSend: !!(ws && leadDetail && coordinates && email),
  };
};
